import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    RefreshCw, Info, ArrowUpDown, ArrowUp, ArrowDown, ChevronRight, Loader2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getCrmSection } from '../../../api/kpi';
import { PeriodSelector, useAnalyticsPeriod } from './AnalyticsPeriod';
import AgentProfileModal from './AgentProfileModal';
import { CLR, TEAL, fmtNumber, formatValue, initials } from './crmAnalyticsUi';

// Re-exported for the panels and tests that already reach for it here.
export { formatValue };

/**
 * Analytics > CRM Analytics > every per-domain tab.
 *
 * ONE component renders Prospecting, Database, Leads, Buyers, Inspections,
 * Tasks, Appraisals, Listings, Revenue, Conversion and the Executive views.
 * That is possible because the API returns the same envelope for all of them
 * ({cards, columns, rows, totals, breakdowns, funnel, notes, meta}) — the
 * backend decides what a section means, this decides how it looks. Adding a
 * section is a backend change plus one entry in analyticsTabs.js.
 *
 * Three things it deliberately does:
 *
 *   - Shows the measurement notes. Several of Christian's metrics cannot be
 *     computed from what the CRM records (call connect rates, door knocks, per
 *     agent email opens). The panel surfaces those caveats next to the numbers
 *     instead of letting a confident-looking zero stand.
 *   - Renders the team row from the API's totals, never by summing the visible
 *     rows, so a rate on the team line is the real agency rate.
 *   - Puts the filters the section actually supports in the bar and nothing
 *     else, driven by meta.supports_filters.
 */

const FILTER_DEFS = {
    role: {
        label: 'Role',
        options: [
            { value: '', label: 'Everyone' },
            { value: 'agent', label: 'Agents' },
            { value: 'admin', label: 'Admins' },
            { value: 'super_admin', label: 'Super Admins' },
            { value: 'pa', label: 'PAs' },
        ],
    },
    kind: {
        label: 'Lead type',
        options: [
            { value: '', label: 'All leads' },
            { value: 'selling', label: 'Selling leads' },
            { value: 'buyer', label: 'Buyer leads' },
        ],
    },
    source: {
        label: 'Source',
        options: [
            { value: '', label: 'Any source' },
            { value: 'website', label: 'Website' },
            { value: 'referral', label: 'Referral' },
            { value: 'ofi', label: 'Open for inspection' },
            { value: 'rea', label: 'REA' },
            { value: 'domain', label: 'Domain' },
            { value: 'buyer_portal', label: 'Buyer portal' },
            { value: 'cold_call', label: 'Cold call' },
            { value: 'social_media', label: 'Social media' },
        ],
    },
    market: {
        label: 'Market',
        options: [
            { value: '', label: 'On + off market' },
            { value: 'on', label: 'On market' },
            { value: 'off', label: 'Off market' },
        ],
    },
};

const BAND_COLOURS = {
    Green: { bg: '#DCFCE7', fg: '#166534' },
    Amber: { bg: '#FEF3C7', fg: '#92400E' },
    Red: { bg: '#FEE2E2', fg: '#991B1B' },
};

const StatCard = ({ card }) => (
    <div
        className="rounded-xl border bg-white p-4"
        style={{ borderColor: CLR.border }}
        title={card.hint || ''}
    >
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            {card.label}
        </p>
        <p className="mt-1 text-2xl font-bold text-gray-900">
            {formatValue(card.value, card.format)}
        </p>
        {card.hint ? (
            <p className="mt-1 text-[11px] leading-snug text-gray-400">{card.hint}</p>
        ) : null}
    </div>
);

const Funnel = ({ stages }) => {
    if (!stages || stages.length === 0) return null;
    const top = Math.max(...stages.map((s) => Number(s.value) || 0), 1);
    return (
        <div className="rounded-xl border bg-white p-5" style={{ borderColor: CLR.border }}>
            <h4 className="text-sm font-bold text-gray-900">Conversion funnel</h4>
            <p className="mb-3 text-xs text-gray-500">
                Relationships worked through to revenue
            </p>
            <div className="space-y-2">
                {stages.map((stage) => {
                    const width = Math.max((Number(stage.value) || 0) * 100 / top, 1.5);
                    return (
                        <div key={stage.key} className="flex items-center gap-3">
                            <span className="w-40 flex-shrink-0 text-xs font-medium text-gray-600">
                                {stage.label}
                            </span>
                            <div className="h-7 flex-1 overflow-hidden rounded-md bg-gray-100">
                                <div
                                    className="flex h-full items-center justify-end rounded-md px-2 text-[11px] font-bold text-white"
                                    style={{ width: `${width}%`, background: TEAL }}
                                >
                                    {fmtNumber(stage.value)}
                                </div>
                            </div>
                            <span className="w-14 flex-shrink-0 text-right text-[11px] text-gray-400">
                                {stage.pct_of_top ? `${stage.pct_of_top}%` : ''}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const Breakdown = ({ breakdown }) => {
    const rows = breakdown.rows || [];
    const max = Math.max(...rows.map((r) => Number(r.value) || 0), 1);
    return (
        <div className="rounded-xl border bg-white p-5" style={{ borderColor: CLR.border }}>
            <h4 className="text-sm font-bold text-gray-900">{breakdown.title}</h4>
            {breakdown.subtitle ? (
                <p className="mb-3 text-xs text-gray-500">{breakdown.subtitle}</p>
            ) : null}
            {rows.length === 0 ? (
                <p className="py-4 text-center text-xs text-gray-400">Nothing to show yet.</p>
            ) : (
                <div className="space-y-2">
                    {rows.map((row) => (
                        <div key={row.label}>
                            <div className="flex items-baseline justify-between gap-2 text-xs">
                                <span className="truncate text-gray-700">{row.label}</span>
                                <span className="flex-shrink-0 font-semibold text-gray-900">
                                    {formatValue(row.value, breakdown.format || 'int')}
                                </span>
                            </div>
                            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-100">
                                <div
                                    className="h-full rounded-full"
                                    style={{
                                        width: `${Math.max((Number(row.value) || 0) * 100 / max, 1)}%`,
                                        background: TEAL,
                                    }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const Cell = ({ row, column }) => {
    const value = row[column.key];
    if (column.key === 'discipline_band' && BAND_COLOURS[value]) {
        const tone = BAND_COLOURS[value];
        return (
            <span
                className="rounded-md px-2 py-0.5 text-[11px] font-semibold"
                style={{ background: tone.bg, color: tone.fg }}
            >
                {value}
            </span>
        );
    }
    // 'value' means "use the format this row declares", for metric-per-row
    // tables like Executive > Growth where each row has its own unit.
    const format = column.format === 'value' ? (row.value_format || 'int') : column.format;
    return <span>{formatValue(value, format)}</span>;
};

const CrmSectionPanel = ({ section, title, subtitle, reloadToken }) => {
    const period = useAnalyticsPeriod();
    const { start, end } = period.range;

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filters, setFilters] = useState({});
    const [sort, setSort] = useState({ key: null, dir: 'desc' });
    const [showNotes, setShowNotes] = useState(false);
    const [profileAgent, setProfileAgent] = useState(null);

    // Switching tab throws the previous section's payload away BEFORE the new
    // one arrives. Keeping it meant a tab showed the tab you just left — the
    // Growth tab rendering the Funnel's cards and chart until the fetch
    // returned — which reads as the wrong numbers, not as loading.
    // Filters go too: one the previous section supported and this one does not
    // would silently narrow the new tab.
    useEffect(() => {
        setData(null);
        setFilters({});
        setSort({ key: null, dir: 'desc' });
    }, [section]);

    const load = useCallback(async (opts = {}) => {
        setLoading(true);
        setError('');
        try {
            const res = await getCrmSection(section, {
                start_date: start,
                end_date: end,
                ...filters,
                ...(opts.refresh ? { refresh: 1 } : {}),
            });
            setData(res.data);
        } catch (err) {
            console.error(`Failed to load CRM section ${section}:`, err);
            setError('Could not load this section.');
            toast.error('Could not load this section');
        } finally {
            setLoading(false);
        }
    }, [section, start, end, filters]);

    useEffect(() => { load(); }, [load, reloadToken]);

    const supported = data?.meta?.supports_filters || [];
    const entityLabel = data?.meta?.entity_label || 'Agent';
    const columns = data?.columns || [];

    const rows = useMemo(() => {
        const list = [...(data?.rows || [])];
        if (!sort.key) return list;
        return list.sort((a, b) => {
            const av = a[sort.key];
            const bv = b[sort.key];
            if (typeof av === 'string' || typeof bv === 'string') {
                return sort.dir === 'asc'
                    ? String(av ?? '').localeCompare(String(bv ?? ''))
                    : String(bv ?? '').localeCompare(String(av ?? ''));
            }
            return sort.dir === 'asc' ? (av || 0) - (bv || 0) : (bv || 0) - (av || 0);
        });
    }, [data, sort]);

    const toggleSort = (key) => setSort((prev) => (
        prev.key === key
            ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
            : { key, dir: 'desc' }
    ));

    const SortIcon = ({ column }) => {
        if (sort.key !== column) return <ArrowUpDown className="ml-1 inline h-3 w-3 text-gray-300" />;
        return sort.dir === 'desc'
            ? <ArrowDown className="ml-1 inline h-3 w-3" style={{ color: TEAL }} />
            : <ArrowUp className="ml-1 inline h-3 w-3" style={{ color: TEAL }} />;
    };

    return (
        <div className="relative space-y-5">
            {/* Controls */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                    {supported.map((key) => {
                        const def = FILTER_DEFS[key];
                        if (!def) return null;
                        return (
                            <label key={key} className="flex items-center gap-1.5 text-xs text-gray-500">
                                <span className="font-medium">{def.label}</span>
                                <select
                                    value={filters[key] || ''}
                                    onChange={(e) => setFilters((prev) => ({
                                        ...prev, [key]: e.target.value,
                                    }))}
                                    className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs font-medium text-gray-700"
                                >
                                    {def.options.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </label>
                        );
                    })}
                </div>
                <div className="flex items-center gap-2">
                    <PeriodSelector period={period} />
                    <button
                        onClick={() => load({ refresh: true })}
                        disabled={loading}
                        className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                    >
                        <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                </div>
            </div>

            {/* A period or filter change keeps the table in place and dims it,
                so the page does not jump; a tab change has already cleared the
                data above and falls through to the full spinner. */}
            {loading && data ? (
                <div className="pointer-events-none absolute inset-0 z-10 flex items-start justify-center bg-white/60 pt-32">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
            ) : null}

            {loading && !data ? (
                <div className="flex flex-col items-center justify-center gap-2 py-24 text-gray-400">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span className="text-xs font-medium">Loading…</span>
                </div>
            ) : error ? (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-center text-sm text-rose-700">
                    {error}
                </div>
            ) : data ? (
                <>
                    {/* Headline cards */}
                    {data.cards?.length ? (
                        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                            {data.cards.map((c) => <StatCard key={c.key} card={c} />)}
                        </div>
                    ) : null}

                    {data.funnel?.length ? <Funnel stages={data.funnel} /> : null}

                    {/* The table */}
                    {columns.length && data.rows?.length ? (
                        <div className="overflow-hidden rounded-xl border bg-white" style={{ borderColor: CLR.border }}>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b bg-gray-50 text-left" style={{ borderColor: CLR.border }}>
                                            <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                                {entityLabel}
                                            </th>
                                            {columns.map((column) => (
                                                <th
                                                    key={column.key}
                                                    onClick={() => toggleSort(column.key)}
                                                    title={column.hint || ''}
                                                    className="cursor-pointer whitespace-nowrap px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-gray-500 hover:text-gray-700"
                                                >
                                                    {column.label}
                                                    <SortIcon column={column.key} />
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row) => (
                                            <tr
                                                key={row.agent_id ?? row.agent_name}
                                                className={`border-b last:border-0 ${row.agent_id ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                                                style={{ borderColor: CLR.border }}
                                                onClick={row.agent_id ? () => setProfileAgent(row) : undefined}
                                            >
                                                <td className="px-4 py-2.5">
                                                    <div className="flex items-center gap-2">
                                                        {row.agent_id ? (
                                                            <span
                                                                className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white"
                                                                style={{ background: TEAL }}
                                                            >
                                                                {initials(row.agent_name)}
                                                            </span>
                                                        ) : null}
                                                        <span className="whitespace-nowrap font-medium text-gray-900">
                                                            {row.agent_name}
                                                        </span>
                                                        {row.agent_id ? (
                                                            <ChevronRight className="h-3.5 w-3.5 text-gray-300" />
                                                        ) : null}
                                                    </div>
                                                </td>
                                                {columns.map((column) => (
                                                    <td key={column.key} className="whitespace-nowrap px-3 py-2.5 text-right text-gray-700">
                                                        <Cell row={row} column={column} />
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                        {data.totals && Object.keys(data.totals).length ? (
                                            <tr className="bg-gray-50 font-semibold">
                                                <td className="px-4 py-2.5 text-gray-700">Team</td>
                                                {columns.map((column) => (
                                                    <td key={column.key} className="whitespace-nowrap px-3 py-2.5 text-right text-gray-900">
                                                        <Cell row={data.totals} column={column} />
                                                    </td>
                                                ))}
                                            </tr>
                                        ) : null}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : null}

                    {/* Breakdowns */}
                    {data.breakdowns?.length ? (
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                            {data.breakdowns.map((b) => <Breakdown key={b.key} breakdown={b} />)}
                        </div>
                    ) : null}

                    {/* Measurement notes */}
                    {data.notes?.length ? (
                        <div className="rounded-xl border bg-blue-50/40 p-4" style={{ borderColor: '#BFDBFE' }}>
                            <button
                                onClick={() => setShowNotes((v) => !v)}
                                className="flex items-center gap-2 text-xs font-semibold text-blue-800"
                            >
                                <Info className="h-3.5 w-3.5" />
                                How these numbers are measured
                                <span className="text-blue-400">{showNotes ? '▲' : '▼'}</span>
                            </button>
                            {showNotes ? (
                                <ul className="mt-2 space-y-1.5 pl-5">
                                    {data.notes.map((note) => (
                                        <li key={note} className="list-disc text-xs leading-relaxed text-blue-900/80">
                                            {note}
                                        </li>
                                    ))}
                                </ul>
                            ) : null}
                        </div>
                    ) : null}
                </>
            ) : null}

            {profileAgent ? (
                <AgentProfileModal
                    agentId={profileAgent.agent_id}
                    agentName={profileAgent.agent_name}
                    startDate={start}
                    endDate={end}
                    onClose={() => setProfileAgent(null)}
                />
            ) : null}
        </div>
    );
};

export default CrmSectionPanel;
